import React from 'react';

import { plural, useAsyncWrapper } from '@neptune/shared/common-util';
import {
  FilterResult,
  FilterResultItem,
  SEARCH_MODES,
  SearchMode,
} from '@neptune/shared/search-domain';

import { AttributeDefinition } from 'domain/experiment/attribute';

import { highlightAttributeSearchLabels } from './highlight-attribute-search-labels';
import { ScopedAttributeDefinitionPrioritizedResult } from './use-scoped-attribute-definition-prioritized-data-source';

const EMPTY_ENTRIES: FilterResultItem<AttributeDefinition>[] = [];

export const useSearchAttributesPrioritized = (
  dataSource: ScopedAttributeDefinitionPrioritizedResult,
  query: string,
): FilterResult<AttributeDefinition & { full?: string }> & {
  error?: any;
  message: string;
  loading: boolean;
  enterFullSearchMode: () => void;
  prioritizedMatchesMap?: Map<string, boolean>;
} => {
  const [searchMode, setSearchMode] = React.useState<SearchMode | undefined>(undefined);

  const dataSourceWithHighlight = React.useCallback(
    async (search: string, mode?: SearchMode) => {
      const attributesResult = await dataSource(search, mode);

      const full = highlightAttributeSearchLabels(
        attributesResult.entries.map(({ original }) => original.name),
      );

      const entries = attributesResult.entries.map((entry, i) => ({
        ...entry,
        original: {
          ...entry.original,
          full: full[i],
        },
      }));

      return {
        entries,
        searchMode: attributesResult.searchMode,
        thereIsMore: attributesResult.thereIsMore,
        priorityMatches: attributesResult.priorityMatches,
        needsFullSearch: attributesResult.needsFullSearch,
      };
    },
    [dataSource],
  );

  const resolver = React.useCallback(
    async (mode?: SearchMode) => {
      return dataSourceWithHighlight(query, mode);
    },
    [dataSourceWithHighlight, query],
  );

  const { resolve, result, ...metadata } = useAsyncWrapper({
    resolver,
  });

  const enterFullSearchMode = React.useCallback(() => {
    setSearchMode(SEARCH_MODES.FULL);
    resolve(SEARCH_MODES.FULL);
  }, [resolve]);

  React.useEffect(() => {
    setSearchMode(undefined);
    resolve(undefined);
  }, [query, resolve]);

  React.useEffect(() => {
    if (metadata.error) {
      return;
    }

    if (metadata.loading) {
      return;
    }

    if (!result?.needsFullSearch) {
      return;
    }

    if (result?.entries.length > 0) {
      return;
    }

    enterFullSearchMode();
  }, [
    metadata.loading,
    metadata.error,
    enterFullSearchMode,
    result?.needsFullSearch,
    result?.entries.length,
  ]);

  const loading = metadata.loading;
  const attributes = result?.entries ?? [];

  const message = React.useMemo(() => {
    const resultsLabel = plural(attributes.length, { one: 'metric', other: 'metrics' });

    if (loading) {
      if (searchMode === SEARCH_MODES.FULL) {
        return 'Searching across all runs. This can take a few seconds...';
      }

      return 'Searching...';
    }

    if (metadata.error) {
      return "Couldn't load attributes. Please try again.";
    }

    if (attributes.length === 0) {
      if (result?.needsFullSearch) {
        return `No matching results. Please try a full search mode to browse all available metrics.`;
      }

      return 'No matching metrics found.';
    }

    if (searchMode !== SEARCH_MODES.FULL) {
      return `Showing ${attributes.length} ${resultsLabel} from currently selected runs.`;
    }

    return `Showing ${attributes.length} ${resultsLabel}:`;
  }, [attributes.length, searchMode, loading, metadata.error, result?.needsFullSearch]);

  return {
    ...metadata,
    searchMode,
    enterFullSearchMode,
    entries: result?.entries ?? EMPTY_ENTRIES,
    thereIsMore: result?.thereIsMore ?? false,
    prioritizedMatchesMap: result?.priorityMatches,
    needsFullSearch: (searchMode !== SEARCH_MODES.FULL && result?.needsFullSearch) ?? false,
    message,
  };
};
